import axios from 'axios';
import { plainToInstance } from 'class-transformer';

import { ApiResponse, Receipt } from '../model';
import { CollectionApiResponse } from './../model/CollectionApiResponse';
import { TEMP_EMPTY_FOR_PREVIEW } from './temp-empty-preview';

export interface CheckReceiptParams {
  description: string;
  receipt_items: Array<{
    total_amount: number;
    amount: number;
    duration?: number;
    medicine_name: string;
    frequency_name: string;
    eating_name?: string;
    daytime_name: string;
  }>;
}

export interface CreateReceiptParams {
  patient_id: number | null;
  address: number;
  description: string;
  payment_method: number;
  analysis_result: CheckReceiptResponse[];
  scanned_receipts?: number[];
  recipient: {
    surname: string;
    name: string;
    midname?: string;
    birthdate?: string;
    email?: string;
  };
  receipt_items: Array<{
    amount: number;
    duration: number;
    medicine_id: number;
    frequency_id: number;
    /** Справочник 205 «до/после еды»; опционален — если справочник недоступен, не отправляем. */
    eating_id?: number;
    daytime_id: number;
    start: string;
  }>;
}

interface GetReceiptParams {
  date_from?: string;
  date_to?: string;
  people_main_id?: number;
}

interface CreateReceiptResponse {
  receipt_id: number;
}

interface CheckReceiptResponse {
  message: string;
  level: 'info' | 'warning' | 'danger';
}

export interface FreeFormReceiptParams {
  patient_id: number | null;
  text: string;
  scanned_receipts?: number[];
}

interface FreeFormReceiptResponse {
  id: number;
}

export interface ReceiptPreviewItemParams {
  medicine_id: number;
  /** Количество таблеток за один приём. */
  amount: number;
  /** Длительность курса в днях. */
  duration: number;
  frequency_id: number;
  start?: string;
  daytime_id?: number;
  eating_id?: number;
}

export interface ReceiptPreviewParams {
  receipt_items: ReceiptPreviewItemParams[];
}

/** Позиция расчёта. Деньги — целые рубли (как у заказа). */
export interface ReceiptPreviewItem {
  medicine_id: number;
  medicine_price_id: number;
  total_tablets: number;
  /** Количество упаковок. */
  amount: number;
  pack_size: number;
  /** Стоимость позиции, целые рубли. */
  price: number;
}

export interface MedicineWithoutPrice {
  medicine_id: number;
  medicine_name: string;
}

export interface ReceiptPreviewResponse {
  order_items: ReceiptPreviewItem[];
  /** Стоимость позиций, целые рубли. */
  price: number;
  delivery_price: number;
  package_fee: number;
  vat_amount: number;
  total_price: number;
  /** Препараты без актуальной цены — бэкенд пропускает их при расчёте. */
  medicines_without_price: MedicineWithoutPrice[];
}

export class ReceiptApi {
  static readonly KEY = 'receipt';

  static async check(
    body?: CheckReceiptParams,
  ): Promise<CheckReceiptResponse[]> {
    const { data } = await axios.post<
      CollectionApiResponse<CheckReceiptResponse>
    >('/api/v1/receipt/check', body);
    return data.items;
  }

  static async create(
    body?: CreateReceiptParams,
  ): Promise<CreateReceiptResponse> {
    const { data } = await axios.post<ApiResponse<CreateReceiptResponse>>(
      '/api/v1/receipt',
      body,
    );
    return data.data;
  }

  static async clientCreate(
    body?: CreateReceiptParams,
  ): Promise<CreateReceiptResponse> {
    const { data } = await axios.post<ApiResponse<CreateReceiptResponse>>(
      '/api/v1/client/receipt/create',
      body,
    );
    return data.data;
  }

  /**
   * Предпросмотр стоимости: бэкенд считает упаковки и цену по тем же
   * receipt_items, что уйдут в заказ. Ответ — ApiResponse с полями как у
   * заказа: order_items, price, delivery_price, package_fee, vat_amount,
   * total_price.
   */
  static async preview(
    body: ReceiptPreviewParams,
  ): Promise<ReceiptPreviewResponse> {
    const { data } = await axios.post<
      ApiResponse<Partial<ReceiptPreviewResponse>>
    >('/api/v1/receipt/preview', body);

    const payload = data.data ?? {};

    return {
      order_items: payload.order_items ?? [],
      price: payload.price ?? 0,
      delivery_price: payload.delivery_price ?? 0,
      package_fee: payload.package_fee ?? 0,
      vat_amount: payload.vat_amount ?? 0,
      total_price: payload.total_price ?? 0,
      medicines_without_price: payload.medicines_without_price ?? [],
    };
  }

  static async get(params?: GetReceiptParams): Promise<Receipt[]> {
    if (TEMP_EMPTY_FOR_PREVIEW) return [];
    const { data } = await axios.get<CollectionApiResponse<Receipt>>(
      '/api/v1/client/receipt',
      {
        params,
      },
    );

    return plainToInstance(Receipt, data.items);
  }

  static async getById(id: number): Promise<Receipt> {
    const { data } = await axios.get<ApiResponse<Receipt>>(
      `/api/v1/client/receipt/${id}`,
    );

    return plainToInstance(Receipt, data.data);
  }

  static async freeFormCreate(
    body: FreeFormReceiptParams,
  ): Promise<FreeFormReceiptResponse> {
    const { data } = await axios.post<ApiResponse<FreeFormReceiptResponse>>(
      '/api/v1/client/receipt/free-form',
      body,
    );
    return data.data;
  }

  static async uploadDocument(
    form_data: FormData,
  ): Promise<ApiResponse<{ document_id: number }>> {
    const { data } = await axios.post<ApiResponse<{ document_id: number }>>(
      '/api/v1/receipt/document',
      form_data,
    );
    return data;
  }
}
